import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'users';

// Admin emails that have platform admin access
const ADMIN_EMAILS = ['anddhensoftware@gmail.com'];

// Check if email is admin
export const isAdminEmail = (email) => {
  return ADMIN_EMAILS.includes(email?.toLowerCase());
};

// Determine user role based on email and database
export const determineUserRole = async (user) => {
  if (!user || !user.email) return 'user';
  
  // Check if admin email
  if (isAdminEmail(user.email)) {
    return 'admin';
  }
  
  // Check if user is assigned as manager in database
  try {
    const userDoc = await getUserById(user.uid);
    if (userDoc.user && userDoc.user.role === 'manager') {
      return 'manager';
    }
  } catch (error) {
    console.error('Error determining user role:', error);
  }
  
  return 'user';
};

// Get user by ID
export const getUserById = async (userId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        user: { id: docSnap.id, ...docSnap.data() }, 
        error: null 
      };
    }
    return { user: null, error: null }; // User doesn't exist yet
  } catch (error) {
    console.error('Error getting user:', error);
    return { user: null, error: error.message };
  }
};

// Get user by email
export const getUserByEmail = async (email) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('email', '==', email.toLowerCase())
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { user: null, error: null };
    }
    
    const doc = querySnapshot.docs[0];
    return { 
      user: { id: doc.id, ...doc.data() }, 
      error: null 
    };
  } catch (error) {
    console.error('Error getting user by email:', error);
    return { user: null, error: error.message };
  }
};

// Create or update user
export const createOrUpdateUser = async (userId, userData) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    const userDoc = await getDoc(userRef);
    
    // Determine role
    const role = await determineUserRole({ uid: userId, email: userData.email });
    
    if (userDoc.exists()) {
      // Update existing user - don't overwrite role if already manager
      const existingData = userDoc.data();
      const updateData = {
        ...userData,
        email: userData.email?.toLowerCase(),
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Only update role if not already a manager (to prevent demotion)
      if (existingData.role !== 'manager') {
        updateData.role = role;
      }
      
      await updateDoc(userRef, updateData);
    } else {
      // Create new user
      await setDoc(userRef, {
        ...userData,
        email: userData.email?.toLowerCase(),
        role,
        status: 'active',
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return { error: null };
  } catch (error) {
    console.error('Error creating/updating user:', error);
    return { error: error.message };
  }
};

// Get managers by company
export const getManagersByCompany = async (companyId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('companyId', '==', companyId),
      where('role', '==', 'manager')
    );
    const querySnapshot = await getDocs(q);
    
    const managers = [];
    querySnapshot.forEach((doc) => {
      managers.push({ id: doc.id, ...doc.data() });
    });
    
    return { managers, error: null };
  } catch (error) {
    console.error('Error getting managers:', error);
    return { managers: [], error: error.message };
  }
};

// Get all managers (admin view)
export const getAllManagers = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('role', '==', 'manager')
    );
    const querySnapshot = await getDocs(q);
    
    const managers = [];
    querySnapshot.forEach((doc) => {
      managers.push({ id: doc.id, ...doc.data() });
    });
    
    return { managers, error: null };
  } catch (error) {
    console.error('Error getting all managers:', error);
    return { managers: [], error: error.message };
  }
};

// Get all users (admin view)
export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt desc
    users.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { users, error: null };
  } catch (error) {
    console.error('Error getting all users:', error);
    return { users: [], error: error.message };
  }
};

// Assign manager to company
export const assignManager = async (userId, companyId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      role: 'manager',
      companyId,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error assigning manager:', error);
    return { error: error.message };
  }
};

// Remove manager role
export const removeManager = async (userId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      role: 'user',
      companyId: null,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error removing manager:', error);
    return { error: error.message };
  }
};

// Update user profile
export const updateUserProfile = async (userId, data) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { error: error.message };
  }
};

// Toggle user status
export const toggleUserStatus = async (userId, status) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(userRef, {
      status,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error toggling user status:', error);
    return { error: error.message };
  }
};

// Get user stats (admin)
export const getUserStats = async () => {
  try {
    const result = await getAllUsers();
    
    const stats = {
      total: result.users.length,
      admins: result.users.filter(u => u.role === 'admin').length,
      managers: result.users.filter(u => u.role === 'manager').length,
      users: result.users.filter(u => u.role === 'user' || !u.role).length,
      active: result.users.filter(u => u.status === 'active' || !u.status).length,
      inactive: result.users.filter(u => u.status === 'inactive').length,
    };
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting user stats:', error);
    return { stats: null, error: error.message };
  }
};

// Get all document verifications (admin)
export const getAllDocumentVerifications = async () => {
  try {
    const result = await getAllUsers();
    const verifications = [];
    
    // Create a map of verifier user IDs to their company IDs
    const verifierCompanyMap = {};
    result.users.forEach(user => {
      if (user.companyId && user.role === 'manager') {
        verifierCompanyMap[user.id] = user.companyId;
      }
    });
    
    result.users.forEach(user => {
      if (user.documents) {
        Object.keys(user.documents).forEach(docType => {
          const doc = user.documents[docType];
          if (doc.verificationHistory && doc.verificationHistory.length > 0) {
            doc.verificationHistory.forEach((verification, index) => {
              // If company ID is missing, try to get it from the verifier's user profile
              let companyId = verification.verifiedByCompanyId;
              if (!companyId && verification.verifiedBy && verifierCompanyMap[verification.verifiedBy]) {
                companyId = verifierCompanyMap[verification.verifiedBy];
              }
              
              verifications.push({
                id: `${user.id}_${docType}_${index}`,
                userId: user.id,
                userEmail: user.email,
                userName: user.displayName || user.email,
                documentType: docType,
                documentUrl: doc.url,
                verified: verification.verified,
                verifiedBy: verification.verifiedBy,
                verifiedByName: verification.verifiedByName || 'Unknown',
                verifiedByRole: verification.verifiedByRole || 'unknown',
                verifiedByCompanyId: companyId || null,
                verifiedByCompanyName: verification.verifiedByCompanyName || 'Unknown',
                verifiedAt: verification.verifiedAt,
                bookingId: verification.bookingId || null,
                bookingNumber: verification.bookingNumber || null
              });
            });
          }
        });
      }
    });
    
    // Sort by verification date (newest first)
    verifications.sort((a, b) => {
      const dateA = new Date(a.verifiedAt || 0);
      const dateB = new Date(b.verifiedAt || 0);
      return dateB - dateA;
    });
    
    return { verifications, error: null };
  } catch (error) {
    console.error('Error getting document verifications:', error);
    return { verifications: [], error: error.message };
  }
};
